import { type ChangeEvent, type DragEvent, type FC, useRef, useState } from "react";

import { Button } from "@snapchat/snap-design-system";
import { clsx } from "clsx";

import { LENS_BUNDLE_MAX_BYTES, PACKAGE_UNCOMPRESSED_MAX_BYTES, ZIP_MAX_BYTES } from "../../model";
import { type WebLensBuildOutputMeasurement } from "../../utils/measureWebLensBuildOutput";
import { formatMegabytes } from "../../validation/preChecks";
import styles from "../WebLensPage.module.css";
import {
    DropzoneHint,
    DropzonePrompt,
    PackageBudgetMeter,
    PackageBudgetOver,
    PackageMeta,
    PackageRequirementsTitle,
    ReplacePackageCta,
    RequirementBuiltOutput,
    RequirementBundle,
    RequirementNoSource,
    RequirementUnzipped,
    RequirementZip,
} from "../messages.generated";

interface WebLensPackageDropzoneProps {
    file: File | null;
    /**
     * The page-level measurement of the picked package (useWebLensBuildOutputMeasurement) — the
     * same value that drives the rail's budget row and the submit gate, passed in so the meter
     * can never disagree with them. Null renders no meter.
     */
    buildOutput: WebLensBuildOutputMeasurement | null;
    /** True while a submission/update is in flight — new picks are ignored (see onPick note). */
    disabled: boolean;
    /**
     * Called with every accepted pick. Callers reset their checklist here: a failed run freezes
     * its check rows in the rail, and without clearing them a file swap would leave the page
     * describing a run of one file while the dropzone shows another.
     */
    onPick: (file: File) => void;
    /** The archive cap the requirements list states; the push page passes its 50 MiB pipeline cap. */
    archiveMaxBytes?: number;
    /** The publish-budget requirement doesn't bound a push-to-device preview, so that page hides it. */
    showPublishBudget?: boolean;
}

/**
 * The package dropzone shared by the submit and update pages: drop target, browse/replace
 * affordances and the hidden input. The dashed box is the drop target; the click affordance
 * inside it is always a real button, so keyboard and screen-reader users get the same picker
 * without a key shim (role="presentation" says so — the box itself is decoration, and dropping
 * a file is a mouse-only shortcut to the same picker).
 */
export const WebLensPackageDropzone: FC<WebLensPackageDropzoneProps> = ({
    file,
    buildOutput,
    disabled,
    onPick,
    archiveMaxBytes = ZIP_MAX_BYTES,
    showPublishBudget = true,
}) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);
        if (disabled) {
            return;
        }
        const dropped = event.dataTransfer.files[0];
        if (dropped) {
            onPick(dropped);
        }
    };

    // dragleave also fires when the pointer crosses onto a child, which would flicker the active
    // border while a file is dragged around inside the zone. Ignoring leaves that land on our own
    // subtree keeps the children clickable, which the Replace button needs.
    const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
        const enteredElement = event.relatedTarget instanceof Node ? event.relatedTarget : null;
        if (!event.currentTarget.contains(enteredElement)) {
            setIsDragActive(false);
        }
    };

    const onFilePicked = (event: ChangeEvent<HTMLInputElement>) => {
        const picked = event.target.files?.[0];
        if (picked && !disabled) {
            onPick(picked);
        }
        // Clear so re-selecting the same (locally modified) file still fires onChange.
        event.target.value = "";
    };

    const openFilePicker = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    return (
        <>
            <div
                role="presentation"
                className={clsx(styles.dropzone, isDragActive && styles.dropzoneActive)}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragActive(true);
                }}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                {file ? (
                    <>
                        <div className={styles.packageDetails}>
                            <div className={styles.packageName}>{file.name}</div>
                            <div className={styles.packageMeta}>
                                <PackageMeta size={formatMegabytes(file.size)} />
                            </div>
                            {buildOutput ? (
                                <div
                                    className={clsx(
                                        styles.packageMeta,
                                        buildOutput.compressedBytes > LENS_BUNDLE_MAX_BYTES && styles.budgetOver,
                                    )}
                                    data-testid="web-lens-build-output-meter"
                                >
                                    {buildOutput.compressedBytes > LENS_BUNDLE_MAX_BYTES ? (
                                        <PackageBudgetOver
                                            used={formatMegabytes(buildOutput.compressedBytes)}
                                            limit={formatMegabytes(LENS_BUNDLE_MAX_BYTES)}
                                        />
                                    ) : (
                                        <PackageBudgetMeter
                                            used={formatMegabytes(buildOutput.compressedBytes)}
                                            limit={formatMegabytes(LENS_BUNDLE_MAX_BYTES)}
                                        />
                                    )}
                                </div>
                            ) : null}
                        </div>
                        <Button
                            type="default"
                            size="small"
                            isDisabled={disabled}
                            onClick={openFilePicker}
                            data-testid="web-lens-replace-package"
                        >
                            <ReplacePackageCta />
                        </Button>
                    </>
                ) : (
                    <button
                        type="button"
                        className={styles.dropzoneTrigger}
                        onClick={openFilePicker}
                        aria-disabled={disabled}
                    >
                        <div className={styles.dropzonePrompt}>
                            <DropzonePrompt />
                        </div>
                        <div className={styles.dropzoneHint}>
                            <DropzoneHint />
                        </div>
                    </button>
                )}
            </div>
            <input ref={fileInputRef} className={styles.fileInput} type="file" accept=".zip" onChange={onFilePicked} />
            {/* Every limit the conversion enforces, shown before submit so creators don't first
                meet one as a failed lens. Values mirror lens-bundle's bounds (see ../../model). */}
            <div className={styles.requirementsTitle}>
                <PackageRequirementsTitle />
            </div>
            <ul className={styles.requirementsList} data-testid="web-lens-package-requirements">
                <li>
                    <RequirementZip limit={formatMegabytes(archiveMaxBytes)} />
                </li>
                <li>
                    <RequirementUnzipped size={formatMegabytes(PACKAGE_UNCOMPRESSED_MAX_BYTES)} />
                </li>
                <li>
                    <RequirementBuiltOutput />
                </li>
                <li>
                    <RequirementNoSource />
                </li>
                {showPublishBudget ? (
                    <li>
                        <RequirementBundle limit={formatMegabytes(LENS_BUNDLE_MAX_BYTES)} />
                    </li>
                ) : null}
            </ul>
        </>
    );
};
